import { type KeyboardEvent, type MouseEvent, useRef } from "react";
import { useTranslation } from "react-i18next";
import { cx } from "../cx";

/**
 * Bars from a peaks array (0..1 each), the played part in accent. Click or
 * arrow keys seek when `onSeek` is given; it is a slider for assistive tech.
 */
export function WaveformScrubber({
  peaks,
  progress,
  onSeek,
  height = 28,
  className = "",
}: {
  peaks: ReadonlyArray<number>;
  /** Fraction 0..1 played. */
  progress: number;
  onSeek?: (fraction: number) => void;
  height?: number;
  className?: string;
}) {
  const { t } = useTranslation("components");
  const ref = useRef<HTMLDivElement>(null);
  const fraction = Math.min(1, Math.max(0, Number.isFinite(progress) ? progress : 0));
  const bar = 3;
  const gap = 2;
  const width = peaks.length * (bar + gap) - gap;
  const playedBars = Math.round(fraction * peaks.length);
  const bars = peaks.map((peak, i) => ({ x: i * (bar + gap), peak, played: i < playedBars }));

  const seekFromEvent = (e: MouseEvent<HTMLDivElement>) => {
    if (!onSeek || !ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    if (rect.width <= 0) return;
    onSeek((e.clientX - rect.left) / rect.width);
  };
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (!onSeek) return;
    const step = 0.05;
    if (e.key === "ArrowRight" || e.key === "ArrowUp") onSeek(Math.min(1, fraction + step));
    else if (e.key === "ArrowLeft" || e.key === "ArrowDown") onSeek(Math.max(0, fraction - step));
    else if (e.key === "Home") onSeek(0);
    else if (e.key === "End") onSeek(1);
    else return;
    e.preventDefault();
  };

  return (
    <div
      ref={ref}
      role="slider"
      aria-label={t("waveform.label")}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(fraction * 100)}
      aria-disabled={onSeek ? undefined : true}
      tabIndex={onSeek ? 0 : -1}
      title={onSeek ? t("waveform.hint") : undefined}
      onClick={seekFromEvent}
      onKeyDown={onKeyDown}
      data-component="WaveformScrubber"
      className={cx("flex-1", onSeek && "cursor-pointer", className)}
      style={{ height }}
    >
      <svg
        viewBox={`0 0 ${Math.max(width, 1)} ${height}`}
        preserveAspectRatio="none"
        width="100%"
        height={height}
        aria-hidden="true"
        focusable="false"
      >
        {bars.map(({ x, peak, played }) => {
          const h = Math.max(2, Math.min(1, Math.max(0, peak)) * height);
          return (
            <rect
              key={x}
              x={x}
              y={height - h}
              width={bar}
              height={h}
              fill={played ? "var(--color-accent)" : "var(--color-line)"}
            />
          );
        })}
      </svg>
    </div>
  );
}
